import { ErrorBoundary } from '@sentry/react';
import { AuthProtectedComponent } from '@statsbomb/kitbag-auth';
import { VideoStatus } from '@statsbomb/parachute-types';
import { PlayButton, VideoReference } from '@statsbomb/video-client-js';
import { useSetAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { trackObjectOpenCloseEvent } from '@/analytics/snowplow/analytics';
import { videoLinkAtom } from '@/atoms/navigation';
import { selectVisEventsAtom } from '@/atoms/vis/selection';
import useGetLinkPath from '@/hooks/useGetLinkPath';
import { usePlayButtonFeedback } from '@/hooks/usePlayButtonFeedback';
import { useResetSelectionLayer } from '@/hooks/useResetSelectionLayer';
import { EventDescription, VisEvent } from '@/types/event';
import { formatGameVideoReference, formatGameVideoReferences } from '@/utils/video';

interface PlayVideoButtonProps {
  videoEventId: string;
  eventId: string;
  videoStatus: VideoStatus;
  gameId: string;
  eventDescriptions: EventDescription[];
}

interface PlayGameAggsVideoButtonProps extends Pick<PlayVideoButtonProps, 'videoStatus' | 'gameId'> {
  linkParams: string;
  metricKey?: string;
}

const PlayVideoButtonBase = ({
  videoStatus,
  onClick,
  videoReferences,
  classes,
}: {
  onClick: () => void;
  videoStatus: VideoStatus;
  videoReferences: VideoReference[];
  classes?: string;
}) => {
  const { t } = useTranslation('video');
  const disabledReason = usePlayButtonFeedback(videoStatus);

  return (
    <ErrorBoundary>
      <AuthProtectedComponent>
        <span className={classes} data-testid={`video-play-button-${videoStatus}`}>
          <PlayButton
            disabled={videoStatus !== 'enabled'}
            disabledReason={disabledReason}
            off={videoStatus === 'restricted'}
            icon={videoStatus === 'disabled' ? 'Lock' : 'Play'}
            onClick={onClick}
            size="small"
            title={t('playVideo')}
            variant="ghost"
            videoReferences={videoReferences}
          />
        </span>
      </AuthProtectedComponent>
    </ErrorBoundary>
  );
};

export const PlayVideoButton = ({
  videoEventId,
  eventId,
  period,
  gameId,
  videoStatus,
  eventDescriptions,
}: PlayVideoButtonProps & { period: number }) => {
  const selectVisEvents = useSetAtom(selectVisEventsAtom);
  const resetSelectionLayer = useResetSelectionLayer();
  const navigate = useNavigate();
  const { to } = useGetLinkPath(videoLinkAtom);

  const handleSelect = () => {
    resetSelectionLayer();

    const selectedVisEvent = [
      {
        eventId,
        videoEventId,
        period,
        gameId: Number(gameId),
        videoStatus,
      },
    ] as VisEvent[];
    selectVisEvents(
      selectedVisEvent,
      eventDescriptions.filter(description => description.eventId === eventId),
    );

    navigate(to);
  };

  return (
    <PlayVideoButtonBase
      videoReferences={[formatGameVideoReference(gameId, period)]}
      onClick={handleSelect}
      videoStatus={videoStatus}
    />
  );
};

export const PlayGameAggsVideoButton = ({
  gameId,
  videoStatus,
  linkParams,
  metricKey,
}: PlayGameAggsVideoButtonProps) => {
  const navigate = useNavigate();
  const videoReferences = formatGameVideoReferences(gameId);
  return (
    <PlayVideoButtonBase
      classes="invisible"
      onClick={async () => {
        await trackObjectOpenCloseEvent({
          objectType: 'Video Synch',
          openVsClose: 'Open',
          conditionType: metricKey || 'unknown',
        });
        navigate(`./video/preview/${linkParams}`);
      }}
      videoStatus={videoStatus}
      videoReferences={videoReferences}
    />
  );
};
